// ========================================
// PAGE AdminDashboard
// Tableau de bord principal
// ========================================

import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/Layout/AdminLayout';
import { StatsCard } from '../../components/admin/Dashboard';
import { useProjects, useBlogPosts, useMessages } from '../../hooks';
import { 
  FiEye, 
  FiHeart, 
  FiFolder, 
  FiFileText, 
  FiMail,
  FiTrendingUp,
  FiClock,
  FiPlus,
  FiEdit2,
  FiMessageSquare
} from 'react-icons/fi';
import { Link } from 'react-router-dom';
import './Admin.css';

const AdminDashboard = () => {
  // ========== HOOKS FIREBASE ==========
  const { projects, loading: projectsLoading } = useProjects();
  const { posts, loading: postsLoading } = useBlogPosts(true, null); // Tous les posts (pas de filtre)
  const { messages, loading: messagesLoading } = useMessages();

  // ========== CALCUL DES STATS ==========
  const stats = {
    // Total des vues (projets + articles)
    totalViews: 
      (projects?.reduce((sum, p) => sum + (p.views || 0), 0) || 0) +
      (posts?.reduce((sum, p) => sum + (p.views || 0), 0) || 0),
    
    // Total des likes (projets + articles)
    totalLikes: 
      (projects?.reduce((sum, p) => sum + (p.likes || 0), 0) || 0) +
      (posts?.reduce((sum, p) => sum + (p.likes || 0), 0) || 0),
    
    // Nombre de projets
    totalProjects: projects?.length || 0,
    
    // Nombre d'articles publiés
    totalArticles: posts?.filter(p => p.status === 'published')?.length || 0,
    
    // Messages non lus
    unreadMessages: messages?.filter(m => !m.read)?.length || 0
  };

  // ========== ACTIVITÉ RÉCENTE ==========
  const getRecentActivity = () => {
    const activities = [];

    // Derniers projets ajoutés
    if (projects?.length > 0) {
      const recentProjects = [...projects]
        .sort((a, b) => {
          const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
          const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
          return dateB - dateA;
        })
        .slice(0, 2);

      recentProjects.forEach(project => {
        activities.push({
          id: `project-${project.id}`,
          type: 'project',
          icon: FiFolder,
          message: `Projet "${project.title}" ajouté`,
          time: formatRelativeTime(project.createdAt),
          link: `/admin/projets`
        });
      });
    }

    // Derniers articles publiés
    if (posts?.length > 0) {
      const recentPosts = [...posts]
        .filter(p => p.status === 'published')
        .sort((a, b) => {
          const dateA = a.publishedAt?.toDate ? a.publishedAt.toDate() : new Date(a.publishedAt || 0);
          const dateB = b.publishedAt?.toDate ? b.publishedAt.toDate() : new Date(b.publishedAt || 0);
          return dateB - dateA;
        })
        .slice(0, 2);

      recentPosts.forEach(post => {
        activities.push({
          id: `post-${post.id}`,
          type: 'article',
          icon: FiFileText,
          message: `Article "${post.title}" publié`,
          time: formatRelativeTime(post.publishedAt),
          link: `/admin/blog`
        });
      });
    }

    // Derniers messages reçus
    if (messages?.length > 0) {
      const recentMessages = [...messages]
        .sort((a, b) => {
          const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
          const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
          return dateB - dateA;
        })
        .slice(0, 2);

      recentMessages.forEach(msg => {
        activities.push({
          id: `msg-${msg.id}`,
          type: msg.read ? 'message' : 'unread',
          icon: FiMessageSquare,
          message: `Message de ${msg.name || 'Anonyme'}`,
          time: formatRelativeTime(msg.createdAt),
          link: `/admin/messages`
        });
      });
    }

    // Trier par date et prendre les 5 plus récents
    return activities
      .sort((a, b) => {
        // Convertir "time" relatif en priorité (approximatif)
        const getPriority = (time) => {
          if (time.includes('instant') || time.includes('seconde')) return 1;
          if (time.includes('minute')) return 2;
          if (time.includes('heure')) return 3;
          if (time.includes('jour')) return 4;
          return 5;
        };
        return getPriority(a.time) - getPriority(b.time);
      })
      .slice(0, 5);
  };

  // ========== HELPERS ==========
  const formatRelativeTime = (dateValue) => {
    if (!dateValue) return 'Date inconnue';

    const date = dateValue?.toDate ? dateValue.toDate() : new Date(dateValue);
    const now = new Date();
    const diffMs = now - date;
    const diffSec = Math.floor(diffMs / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHour = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHour / 24);

    if (diffSec < 60) return 'À l\'instant';
    if (diffMin < 60) return `Il y a ${diffMin} minute${diffMin > 1 ? 's' : ''}`;
    if (diffHour < 24) return `Il y a ${diffHour} heure${diffHour > 1 ? 's' : ''}`;
    if (diffDay < 7) return `Il y a ${diffDay} jour${diffDay > 1 ? 's' : ''}`;
    
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  const loading = projectsLoading || postsLoading || messagesLoading;
  const recentActivity = getRecentActivity();

  const quickActions = [
    { label: 'Nouveau projet', path: '/admin/projets', icon: FiFolder, color: 'blue' },
    { label: 'Nouvel article', path: '/admin/blog', icon: FiFileText, color: 'green' },
    { label: 'Voir les messages', path: '/admin/messages', icon: FiMail, color: 'orange', badge: stats.unreadMessages },
  ];

  if (loading) {
    return (
      <AdminLayout>
        <div className="dashboard-loading">
          <div className="spinner"></div>
          <p>Chargement du tableau de bord...</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-dashboard">
        {/* Header */}
        <div className="dashboard-header">
          <div>
            <h1>Tableau de bord</h1>
            <p className="dashboard-subtitle">
              Bienvenue ! Voici un aperçu de votre portfolio.
            </p>
          </div>
          <div className="dashboard-date">
            <FiClock />
            <span>{new Date().toLocaleDateString('fr-FR', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}</span>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="stats-grid">
          <StatsCard 
            icon={FiEye}
            value={stats.totalViews.toLocaleString()}
            label="Vues totales"
            color="blue"
          />
          <StatsCard 
            icon={FiHeart}
            value={stats.totalLikes.toLocaleString()}
            label="Likes totaux"
            color="red"
          />
          <StatsCard 
            icon={FiFolder}
            value={stats.totalProjects}
            label="Projets"
            color="green"
          />
          <StatsCard 
            icon={FiFileText}
            value={stats.totalArticles}
            label="Articles publiés"
            color="purple"
          />
        </div>

        {/* Messages non lus */}
        {stats.unreadMessages > 0 && (
          <div className="unread-messages-banner">
            <FiMail />
            <span>
              Vous avez <strong>{stats.unreadMessages}</strong> message{stats.unreadMessages > 1 ? 's' : ''} non lu{stats.unreadMessages > 1 ? 's' : ''}
            </span>
            <Link to="/admin/messages" className="view-messages-btn">
              Voir les messages
            </Link>
          </div>
        )}

        {/* Content Grid */}
        <div className="dashboard-grid">
          {/* Quick Actions */}
          <div className="dashboard-card">
            <div className="card-header">
              <h2>
                <FiTrendingUp />
                Actions rapides
              </h2>
            </div>
            <div className="quick-actions">
              {quickActions.map((action, index) => (
                <Link 
                  key={index} 
                  to={action.path} 
                  className={`quick-action-btn ${action.color}`}
                >
                  <FiPlus className="action-plus" />
                  <action.icon className="action-icon" />
                  <span>{action.label}</span>
                  {action.badge > 0 && (
                    <span className="action-badge">{action.badge}</span>
                  )}
                </Link>
              ))}
            </div>
          </div>

          {/* Recent Activity */}
          <div className="dashboard-card">
            <div className="card-header">
              <h2>
                <FiClock />
                Activité récente
              </h2>
            </div>
            <div className="activity-list">
              {recentActivity.length > 0 ? (
                recentActivity.map((activity) => (
                  <Link 
                    key={activity.id} 
                    to={activity.link}
                    className="activity-item"
                  >
                    <div className={`activity-dot ${activity.type}`}>
                      <activity.icon />
                    </div>
                    <div className="activity-content">
                      <p>{activity.message}</p>
                      <span className="activity-time">{activity.time}</span>
                    </div>
                  </Link>
                ))
              ) : (
                <div className="activity-empty">
                  <FiClock />
                  <p>Aucune activité récente</p>
                  <span>Commencez par ajouter un projet ou un article</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Résumé rapide */}
        <div className="dashboard-grid">
          {/* Derniers projets */}
          <div className="dashboard-card">
            <div className="card-header">
              <h2>
                <FiFolder />
                Derniers projets
              </h2>
              <Link to="/admin/projets" className="view-all-link">
                Voir tout →
              </Link>
            </div>
            <div className="recent-items-list">
              {projects?.length > 0 ? (
                [...projects]
                  .sort((a, b) => {
                    const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
                    const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
                    return dateB - dateA;
                  })
                  .slice(0, 3)
                  .map(project => (
                    <div key={project.id} className="recent-item">
                      <div className="recent-item-image">
                        {project.mainImage ? (
                          <img src={project.mainImage} alt={project.title} />
                        ) : (
                          <FiFolder />
                        )}
                      </div>
                      <div className="recent-item-info">
                        <h4>{project.title}</h4>
                        <span className={`status-badge ${project.status}`}>
                          {project.status === 'active' ? 'En cours' : 'Terminé'}
                        </span>
                      </div>
                      <div className="recent-item-stats">
                        <span><FiEye /> {project.views || 0}</span>
                        <span><FiHeart /> {project.likes || 0}</span>
                      </div>
                    </div>
                  ))
              ) : (
                <div className="empty-recent">
                  <p>Aucun projet</p>
                  <Link to="/admin/projets" className="add-link">
                    <FiPlus /> Ajouter un projet
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Derniers articles */}
          <div className="dashboard-card">
            <div className="card-header">
              <h2>
                <FiFileText />
                Derniers articles
              </h2>
              <Link to="/admin/blog" className="view-all-link">
                Voir tout →
              </Link>
            </div>
            <div className="recent-items-list">
              {posts?.length > 0 ? (
                [...posts]
                  .sort((a, b) => {
                    const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt || 0);
                    const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt || 0);
                    return dateB - dateA;
                  })
                  .slice(0, 3)
                  .map(post => (
                    <div key={post.id} className="recent-item">
                      <div className="recent-item-image">
                        {post.coverImage ? (
                          <img src={post.coverImage} alt={post.title} />
                        ) : (
                          <FiFileText />
                        )}
                      </div>
                      <div className="recent-item-info">
                        <h4>{post.title}</h4>
                        <span className={`status-badge ${post.status}`}>
                          {post.status === 'published' ? 'Publié' : post.status === 'draft' ? 'Brouillon' : 'Archivé'}
                        </span>
                      </div>
                      <div className="recent-item-stats">
                        <span><FiEye /> {post.views || 0}</span>
                        <span><FiHeart /> {post.likes || 0}</span>
                      </div>
                    </div>
                  ))
              ) : (
                <div className="empty-recent">
                  <p>Aucun article</p>
                  <Link to="/admin/blog" className="add-link">
                    <FiPlus /> Écrire un article
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Getting Started - Affiché uniquement si peu de contenu */}
        {(stats.totalProjects < 3 || stats.totalArticles < 2) && (
          <div className="dashboard-card getting-started">
            <div className="card-header">
              <h2>🚀 Pour commencer</h2>
            </div>
            <div className="getting-started-content">
              <div className="step">
                <div className="step-number">1</div>
                <div className="step-content">
                  <h3>Configurez votre profil</h3>
                  <p>Ajoutez vos informations dans la section "À Propos"</p>
                  <Link to="/admin/a-propos" className="step-link">
                    Configurer →
                  </Link>
                </div>
              </div>
              <div className="step">
                <div className="step-number">2</div>
                <div className="step-content">
                  <h3>Personnalisez l'accueil</h3>
                  <p>Modifiez le contenu de votre page d'accueil</p>
                  <Link to="/admin/accueil" className="step-link">
                    Personnaliser →
                  </Link>
                </div>
              </div>
              <div className="step">
                <div className="step-number">3</div>
                <div className="step-content">
                  <h3>Ajoutez vos projets</h3>
                  <p>Présentez vos réalisations et expériences</p>
                  <Link to="/admin/projets" className="step-link">
                    Ajouter →
                  </Link>
                </div>
              </div>
              <div className="step">
                <div className="step-number">4</div>
                <div className="step-content">
                  <h3>Publiez un article</h3>
                  <p>Partagez vos réflexions et conseils</p>
                  <Link to="/admin/blog" className="step-link">
                    Écrire →
                  </Link>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;